import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Download, Headphones, LogOut, Gift, ShoppingBag, UserRound } from "lucide-react";
import { toast } from "sonner";
import AuthPanel from "@/components/reader/AuthPanel";
import ProfileSettings from "@/components/account/ProfileSettings";
import VerifyEmailBanner from "@/components/account/VerifyEmailBanner";
import RemindersToggle from "@/components/account/RemindersToggle";
import { API } from "@/lib/api";
import { fetchAccount, readerLogout, daysUntil, setTimezone } from "@/lib/reader";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

function Purchases({ items }) {
  return (
    <section className="mt-12" data-testid="account-purchases">
      <h2 className="flex items-center gap-3 font-serif-display text-3xl"><ShoppingBag className="w-6 h-6 text-primary-gold" /> Your purchases</h2>
      {items.length === 0 ? (
        <p className="text-soft text-sm mt-4" data-testid="account-purchases-empty">
          No purchases yet. Books you buy while signed in — or with the same PayPal email, once your email is verified — appear here for re-download.{" "}
          <Link to="/#catalog" className="text-primary-gold">Browse the books →</Link>
        </p>
      ) : (
        <ul className="mt-6 divide-y divide-white/10 border border-white/10">
          {items.map((p) => (
            <li key={p.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-6 py-5" data-testid={`account-purchase-${p.id}`}>
              <div>
                <p className="font-serif-display text-xl">{p.title}</p>
                <p className="text-soft text-xs font-mono-ui mt-1">Bought {fmtDate(p.created_at)} · ${Number(p.amount).toFixed(2)}</p>
              </div>
              <a href={`${API}/download/${p.download_token}`} data-testid={`account-download-${p.id}`} className="btn-gold px-5 py-3 font-mono-ui text-xs uppercase tracking-[0.18em] inline-flex items-center gap-2 self-start">
                <Download className="w-4 h-4" /> Download
              </a>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Gifts({ items }) {
  if (!items.length) return null;
  return (
    <section className="mt-12" data-testid="account-gifts">
      <h2 className="flex items-center gap-3 font-serif-display text-3xl"><Gift className="w-6 h-6 text-primary-gold" /> Gifts you've sent</h2>
      <ul className="mt-6 divide-y divide-white/10 border border-white/10">
        {items.map((g) => (
          <li key={g.code} className="flex items-center justify-between px-6 py-4 text-sm">
            <span className="font-mono-ui">{g.code}</span>
            <span className={g.redeemed_at ? "text-[#4ade80]" : "text-soft"}>{g.redeemed_at ? `Redeemed ${fmtDate(g.redeemed_at)}` : "Not redeemed yet"}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ReaderStatus({ pass }) {
  return (
    <div className="mt-10 border border-primary-gold/30 bg-primary-gold/5 px-6 py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="account-reader-status">
      <p className="flex items-center gap-3 text-sm">
        <Headphones className="w-5 h-5 text-primary-gold" />
        {pass.lifetime ? "E-Reader: complimentary access, no expiry" : pass.active ? `E-Reader pass active · ${daysUntil(pass.expires_at)} days left` : "No active E-Reader pass"}
      </p>
      <Link to="/reader" data-testid="account-open-reader" className="font-mono-ui text-xs uppercase tracking-[0.18em] text-primary-gold">
        {pass.active ? "Open the E-Reader →" : "Get 40 days for $15 →"}
      </Link>
    </div>
  );
}

export default function Account() {
  const [data, setData] = useState(undefined);
  const load = useCallback(() => fetchAccount().then((d) => {
    setData(d);
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) setTimezone(tz).catch(() => {});
  }).catch(() => setData(null)), []);
  useEffect(() => { load(); }, [load]);

  const logout = async () => {
    await readerLogout().catch(() => {});
    toast.success("Signed out.");
    setData(null);
  };

  return (
    <div className="pt-32 pb-24 px-6 sm:px-8 max-w-5xl mx-auto" data-testid="account-page">
      <p className="flex items-center gap-2 font-mono-ui text-xs uppercase tracking-[0.25em] text-primary-gold"><UserRound className="w-4 h-4" /> Your account</p>
      {data === undefined ? (
        <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 text-primary-gold animate-spin" /></div>
      ) : data === null ? (
        <div className="grid lg:grid-cols-12 gap-12 mt-4">
          <div className="lg:col-span-6">
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">One account for <span className="text-primary-gold italic">books & the E-Reader.</span></h1>
            <p className="text-soft mt-6 leading-relaxed">Sign in to re-download the books you've bought, see gifts you've sent and open the E-Reader. Same sign-in works everywhere on the site.</p>
          </div>
          <div className="lg:col-span-6"><AuthPanel initialMode="login" onSignedIn={load} /></div>
        </div>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mt-4">
            <div>
              <h1 className="font-serif-display text-4xl sm:text-5xl" data-testid="account-name">Hello, {data.user.name || data.user.email}</h1>
              <p className="text-soft text-sm mt-2" data-testid="account-email">{data.user.email}</p>
            </div>
            <button onClick={logout} data-testid="account-logout" className="flex items-center gap-2 font-mono-ui text-xs uppercase tracking-[0.18em] text-soft hover:text-primary-gold"><LogOut className="w-4 h-4" /> Sign out</button>
          </div>
          <ReaderStatus pass={data.pass} />
          {!data.user.email_verified && <VerifyEmailBanner email={data.user.email} />}
          <Purchases items={data.purchases} />
          <Gifts items={data.gifts} />
          <ProfileSettings user={data.user} onChanged={load} />
          <RemindersToggle enabled={data.user.reminders_enabled !== false} />
        </>
      )}
    </div>
  );
}
